import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import apiClient from '@/services/apiClient';
import { useComboStickerPrinter } from '@/hooks/useComboStickerPrinter';
import { PrinterStatusCard } from '@/components/comboV2/PrinterStatusCard';
import { BoxLabelDialog } from '@/components/comboV2/BoxLabelDialog';
import { PrintDeskBatches } from '@/components/comboV2/PrintDeskBatches';
import { ChannelPickDialog } from '@/components/comboV2/ChannelPickDialog';

const D = '/combo-v2/print-desk';
const REFRESH_MS = 10000;

export default function ComboV2StickerPrintDesk() {
  const navigate = useNavigate();
  const printer = useComboStickerPrinter();
  const [staffInput, setStaffInput] = useState('');
  const [staffId, setStaffId] = useState('');
  const [waiting, setWaiting] = useState([]);
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [newKeys, setNewKeys] = useState(new Set());
  const [channels, setChannels] = useState({});
  const [askChannel, setAskChannel] = useState(null);
  const seen = useRef(null);

  const loadWaiting = useCallback(() => apiClient.get(`${D}/staff`).then((r) => setWaiting(r.data.staff || [])).catch(() => {}), []);
  const loadBatches = useCallback(async (sid) => {
    const r = await apiClient.get(`${D}/staff/${encodeURIComponent(sid)}/batches`);
    const keys = r.data.batches.map((b) => b.batch_key);
    if (seen.current) {
      const fresh = keys.filter((k) => !seen.current.has(k));
      if (fresh.length) {
        setNewKeys((p) => new Set([...p, ...fresh]));
        toast.info(`${fresh.length} new batch(es) saved by ${r.data.staff_name}`);
        loadWaiting();
      }
    }
    seen.current = new Set([...(seen.current || []), ...keys]);
    setData(r.data);
    setUpdatedAt(new Date());
    setPicked((p) => new Set([...p].filter((k) => keys.includes(k))));
  }, [loadWaiting]);

  useEffect(() => { loadWaiting(); const t = setInterval(loadWaiting, 20000); return () => clearInterval(t); }, [loadWaiting]);
  useEffect(() => {
    if (staffId) loadBatches(staffId).catch((e) => toast.error(e.response?.data?.detail || 'Failed to load batches'));
  }, [staffId, loadBatches]);
  const preparing = data?.batches?.some((b) => b.codes_ready < b.count);
  // Auto-refresh the open staff: every 3 s while codes are being made, else every 10 s; paused while printing or tab hidden.
  useEffect(() => {
    if (!staffId || busy) return undefined;
    const t = setInterval(() => { if (!document.hidden) loadBatches(staffId).catch(() => {}); }, preparing ? 3000 : REFRESH_MS);
    return () => clearInterval(t);
  }, [staffId, preparing, busy, loadBatches]);

  const openStaff = (sid) => {
    const s = (sid || '').trim();
    if (!s) return;
    seen.current = null;
    setNewKeys(new Set());
    setStaffInput(s); setPicked(new Set()); setData(null); setStaffId(s);
  };

  const print = async (keys, picks = channels) => {
    const valid = (b, ch) => (b.pricing?.options || []).some((o) => o.pricing_type === ch);
    const need = [...new Map((data?.batches || []).filter((b) => keys.includes(b.batch_key) && !b.pricing?.channel
      && (b.pricing?.options || []).length > 1 && !valid(b, picks[b.session_id])).map((b) => [b.session_id, b])).values()];
    if (need.length) { setAskChannel({ keys, list: need }); return; }
    setBusy(true);
    try {
      const r = await apiClient.post(`${D}/prepare`, { staff_id: staffId, batch_keys: keys, channels: picks });
      const n = await printer.printSessions(r.data.sessions || []);
      if (n) toast.success(`Printed ${n} combo sticker(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Print failed', { duration: 12000 });
    } finally {
      setBusy(false);
      setPicked(new Set());
      loadBatches(staffId).catch(() => {});
      loadWaiting();
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="print-desk-page">
      <div className="max-w-[1100px] mx-auto space-y-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate('/wms/combo-v2')} data-testid="back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Combo Sticker Print Desk</h1>
            <p className="text-sm text-stone-500">Enter a PP staff ID to print the combos they saved at the PP Scan Station.</p>
          </div>
        </div>
        <PrinterStatusCard device={printer.usbDevice} supported={printer.usbSupported} progress={printer.progress} onConnect={printer.connectPrinter} />
        <Card className="p-4 space-y-3">
          <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); openStaff(staffInput); }}>
            <Input value={staffInput} onChange={(e) => setStaffInput(e.target.value)} placeholder="Type or scan PP staff ID…" className="max-w-sm" autoFocus data-testid="print-desk-staff-input" />
            <Button type="submit" data-testid="print-desk-load-btn"><Search className="h-4 w-4 mr-1" /> Show batches</Button>
          </form>
          <div className="flex flex-wrap gap-2" data-testid="print-desk-waiting-staff">
            {waiting.length === 0 && <span className="text-xs text-stone-500">No staff have stickers waiting.</span>}
            {waiting.map((s) => (
              <button key={s.staff_id} type="button" onClick={() => openStaff(s.staff_id)} data-testid={`waiting-staff-${s.staff_id}`}
                className={`rounded-full border px-3 py-1 text-sm transition-colors ${staffId === s.staff_id ? 'bg-stone-900 text-white border-stone-900' : 'bg-white hover:bg-amber-50 border-stone-300'}`}>
                {s.staff_name} <span className="font-mono text-xs opacity-70">{s.staff_id}</span> · <b>{s.waiting}</b>
              </button>
            ))}
          </div>
        </Card>
        {staffId && !data && <Card className="p-10 text-center text-stone-500">Loading…</Card>}
        {data && <PrintDeskBatches data={data} picked={picked} setPicked={setPicked} busy={busy || !!printer.progress} onPrint={print}
          newKeys={newKeys} updatedAt={updatedAt} refreshSecs={preparing ? 3 : REFRESH_MS / 1000} chosen={channels} />}
      </div>
      <ChannelPickDialog ask={askChannel?.list} chosen={channels} onCancel={() => setAskChannel(null)}
        onChoose={(sid, ch) => setChannels((c) => ({ ...c, [sid]: ch }))}
        onPrint={() => { const { keys } = askChannel; setAskChannel(null); print(keys, channels); }} />
      <BoxLabelDialog jobs={printer.boxJobs} boxDevice={printer.boxDevice} onConnect={printer.connectBoxPrinter} onPrint={printer.printBoxLabels} onSkip={printer.skipBoxLabels} />
    </div>
  );
}
